// MaintenanceSalaryCombinationV2UploadModal.js
import React, { useState, useEffect, useRef } from "react";
import CloseIcon from "@mui/icons-material/Close";
import ExcelJS from "exceljs";
import { CreateTableNew as CreateTable } from "../../../../../common/APICommon";
import { formatDateLong, isNormalizedMatch } from "../../../../../utils/textUtils";
import { FEATURES } from "../../../../../config/features";
import { ConfirmationModal } from "../../../../../common/Modal";

const MaintenanceSalaryCombinationV2UploadModal = ({
  user,
  isOpen,
  onClose,
  onUpload,
  allRules = [],
  allOperationBenefitsData = [],
  allBenefitGroups = [],
  mscCodes = [],
  fetchMaintenanceDetailedData,
  showGeneralMessage
}) => {
  // --- States ---
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const [mscRules, setMscRules] = useState([]);
  const [benefitGroups, setBenefitGroups] = useState([]);
  const [benefits, setBenefits] = useState([]);

  const [pageRules, setPageRules] = useState(1);
  const [pageGroups, setPageGroups] = useState(1);
  const [pageBenefits, setPageBenefits] = useState(1);
  const itemsPerPage = 10;

  // --- Hooks/Refs ---
  const [lookupData, setLookupData] = useState({});
  const fileInputRef = useRef(null);

  const customCellRenderer = (row, accessor) => {
    if (accessor === "effectiveDate") {
      return formatDateLong(row.effectiveDate);
    }
    if (accessor === "errors") {
      return (
        row[accessor] ? (
          <span className="text-red-500 font-semibold text-xs whitespace-normal">{row[accessor]}</span>
        ) : (
          <span className="text-green-600 font-bold text-xs uppercase">✓ Valid</span>
        )
      );
    }
    return <span className="text-gray-700 px-2">{row[accessor] ?? "-"}</span>;
  };

  // --- Helpers ---
  const isSample = (val) => val?.toString().trim().startsWith("<") && val?.toString().trim().endsWith(">");

  const formatDate = (val) => {
    if (!val) return null;
    if (val instanceof Date && !isNaN(val)) {
      return val.toISOString().split("T")[0];
    }
    const parsed = new Date(val);
    if (!isNaN(parsed.getTime())) {
      return parsed.toISOString().split("T")[0];
    }
    return val?.toString()?.trim() || null;
  };

  // Helper to build unique key for MSC matching
  const buildMscComboKey = (empTypeId, deptId, posId, codeId) => {
    return `${empTypeId || 0}_${deptId || 0}_${posId || 0}_${codeId || 0}`;
  };

  // --- Reset & Load Logic ---
  const resetModalStates = () => {
    setActiveTab(0);
    setMscRules([]);
    setBenefitGroups([]);
    setBenefits([]);
    setPageRules(1);
    setPageGroups(1);
    setPageBenefits(1);
    setIsLoading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  useEffect(() => {
    if (!isOpen) {
      resetModalStates();
      setShowConfirmModal(false);
    } else {
      loadAllLookups();
    }
  }, [isOpen]);

  const loadAllLookups = async () => {
    // 16: EmployeeType, 5: Dept, 9: Pos, 100: DIM_Emp_Dept, 101: DIM_Dept_Pos
    const typeIds = [16, 5, 9, 100, 101];
    const data = await fetchMaintenanceDetailedData(typeIds);
    if (data) setLookupData(data);
  };

  // --- Core Processing Logic ---
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsLoading(true);

    const workbook = new ExcelJS.Workbook();
    try {
      await workbook.xlsx.load(file);

      const empTypeArray = lookupData["16"] || [];
      const deptArray = lookupData["5"] || [];
      const posArray = lookupData["9"] || [];
      const dimEmpDept = lookupData["100"] || [];
      const dimDeptPos = lookupData["101"] || [];
      const codeArray = mscCodes || [];

      // Helper to construct duplicate tracking key
      const getComboKey = (date, empType, dept, pos, code) =>
        `${date || ""}_${empType || 0}_${dept || 0}_${pos || 0}_${code || 0}`.toLowerCase();

      // =========================================================
      // --- 1. Process MSC Rules Sheet ---
      // =========================================================
      const sheet1 = workbook.getWorksheet("MaintenanceSalaryCombinations");
      const tempRules = [];
      const ruleCounts = new Map();
      const excelMscKeys = new Set();

      sheet1?.eachRow((row, rowNum) => {
        if (rowNum === 1) return;
        const values = row.values;
        if (!values || values.length < 2 || isSample(values[1])) return;

        const rawDate = values[1];
        const empTypeStr = values[2]?.toString()?.trim() || "";
        const deptStr = values[3]?.toString()?.trim() || "";
        const posStr = values[4]?.toString()?.trim() || "";
        const codeStr = values[5]?.toString()?.trim() || "";
        const dailyRate = parseFloat(values[6]) || 0;

        const effectiveDate = formatDate(rawDate);

        // Resolve Base Objects (Ensure non-empty input before searching)
        const empTypeObj = empTypeStr ? empTypeArray.find(i => isNormalizedMatch(i.description || i.label, empTypeStr)) : null;
        const deptObj = deptStr ? deptArray.find(i =>
          FEATURES.DEPARTMENT_CODE ? isNormalizedMatch(i.code, deptStr) : isNormalizedMatch(i.description || i.label, deptStr)
        ) : null;
        const posObj = posStr ? posArray.find(i =>
          FEATURES.POSITION_CODE ? isNormalizedMatch(i.code, posStr) : isNormalizedMatch(i.positionDescriptionLabel || i.description, posStr)
        ) : null;
        const codeObj = codeStr ? codeArray.find(c => isNormalizedMatch(c.code || c.maintenanceSalaryCombinationCodeCode, codeStr)) : null;

        let errors = [];
        const isDisregardOtherFilter = empTypeObj?.maintenanceSalaryCombinationDisregardOtherFilter ?? false;

        // Base Validations
        if (!effectiveDate) errors.push("Effective Date is required");

        if (!empTypeStr) errors.push("Employee Type is required");
        else if (!empTypeObj) errors.push(`Employee Type '${empTypeStr}' invalid`);

        if (!codeStr) errors.push("Salary Code is required");
        else if (!codeObj) errors.push(`Salary Code '${codeStr}' not found`);

        if (isNaN(dailyRate) || dailyRate <= 0) errors.push("Daily Rate must be > 0");

        // Conditional Department & Position Validations (When filters are required)
        if (isDisregardOtherFilter !== true) {
          // Department checks
          if (!deptStr) {
            errors.push("Department Code is required");
          } else if (!deptObj) {
            errors.push(`Dept '${deptStr}' not found`);
          } else if (empTypeObj) {
            const isDeptTagged = dimEmpDept.some(d => d.employeeTypeId === empTypeObj.id && d.departmentId === deptObj.id);
            if (!isDeptTagged) errors.push(`Dept '${deptStr}' not tagged to '${empTypeStr}'`);
          }

          // Position checks
          if (!posStr) {
            errors.push("Position Code is required");
          } else if (!posObj) {
            errors.push(`Pos '${posStr}' not found`);
          } else if (deptObj) {
            const isPosTagged = dimDeptPos.some(d => d.departmentId === deptObj.id && d.positionId === posObj.id);
            if (!isPosTagged) errors.push(`Pos '${posStr}' not tagged to Dept '${deptStr}'`);
          }
        }

        const resolvedEmpTypeId = empTypeObj?.id;
        const resolvedDeptId = isDisregardOtherFilter ? null : deptObj?.id;
        const resolvedPosId = isDisregardOtherFilter ? null : posObj?.id;
        const resolvedCodeId = codeObj?.id;

        if (resolvedEmpTypeId && resolvedCodeId && (isDisregardOtherFilter || (resolvedDeptId && resolvedPosId))) {
          const comboKey = buildMscComboKey(resolvedEmpTypeId, resolvedDeptId, resolvedPosId, resolvedCodeId);
          excelMscKeys.add(comboKey);
        }

        // Unique Key for MSC Duplicates Validation
        const ruleDupKey = getComboKey(effectiveDate, resolvedEmpTypeId, resolvedDeptId, resolvedPosId, resolvedCodeId);
        ruleCounts.set(ruleDupKey, (ruleCounts.get(ruleDupKey) || 0) + 1);

        tempRules.push({
          effectiveDate,
          employeeType: empTypeStr,
          department: isDisregardOtherFilter ? "" : deptStr,
          position: isDisregardOtherFilter ? "" : posStr,
          code: codeStr,
          dailyRate,
          errors,
          ruleDupKey,
          employeeTypeId: resolvedEmpTypeId,
          departmentId: resolvedDeptId,
          positionId: resolvedPosId,
          maintenanceSalaryCombinationCodeId: resolvedCodeId,
          maintenanceSalaryCombinationDailyRate: dailyRate
        });
      });

      // Pass 2 for MSC Rules: Attach Duplicate Errors
      const rulesData = tempRules.map(row => {
        const rowErrors = [...row.errors];
        if (ruleCounts.get(row.ruleDupKey) > 1) {
          rowErrors.push("Duplicate entry in Excel sheet");
        }
        return {
          ...row,
          errors: rowErrors.join(", ")
        };
      });

      // =========================================================
      // --- 2. Process Groups Sheet ---
      // =========================================================
      const sheet2 = workbook.getWorksheet("OperationBenefitsGroup");
      const groupsData = [];
      const groupCounts = new Map();
      const excelGroupNames = new Set(); // Track groups present in Sheet 2

      sheet2?.eachRow((row, rowNum) => {
        if (rowNum === 1 || isSample(row.values[1])) return;
        const groupDesc = row.values[1]?.toString()?.trim() || "";
        if (!groupDesc) return;

        const groupKey = groupDesc.toLowerCase();
        groupCounts.set(groupKey, (groupCounts.get(groupKey) || 0) + 1);
        excelGroupNames.add(groupKey);
      });

      sheet2?.eachRow((row, rowNum) => {
        if (rowNum === 1 || isSample(row.values[1])) return;
        const groupDesc = row.values[1]?.toString()?.trim() || "";
        let groupErrors = [];

        if (!groupDesc) groupErrors.push("Group Description is required");
        if (allBenefitGroups.some(g => isNormalizedMatch(g.description, groupDesc))) groupErrors.push("Group Name already exists in DB");

        const groupKey = groupDesc.toLowerCase();
        if (groupCounts.get(groupKey) > 1) {
          groupErrors.push("Duplicate Group Name in Excel");
        }

        groupsData.push({
          description: groupDesc,
          errors: groupErrors.join(", ")
        });
      });

      // =========================================================
      // --- 3. Process Benefits Sheet ---
      // =========================================================
      const sheet3 = workbook.getWorksheet("OperationBenefits");
      const benefitsData = [];

      sheet3?.eachRow((row, rowNum) => {
        if (rowNum === 1 || isSample(row.values[1])) return;
        const values = row.values;
        if (!values || values.length < 2) return;

        const rawDate = values[1];
        const empTypeStr = values[2]?.toString()?.trim() || "";
        const deptStr = values[3]?.toString()?.trim() || "";
        const posStr = values[4]?.toString()?.trim() || "";
        const mscCodeStr = values[5]?.toString()?.trim() || "";
        const operationBenefitGroupStr = values[6]?.toString()?.trim() || "";
        const benefitRate = parseFloat(values[7]) || 0;

        const effectiveDate = formatDate(rawDate);

        const empTypeObj = empTypeStr ? empTypeArray.find(i => isNormalizedMatch(i.description || i.label, empTypeStr)) : null;
        const deptObj = deptStr ? deptArray.find(i =>
          FEATURES.DEPARTMENT_CODE ? isNormalizedMatch(i.code, deptStr) : isNormalizedMatch(i.description || i.label, deptStr)
        ) : null;
        const posObj = posStr ? posArray.find(i =>
          FEATURES.POSITION_CODE ? isNormalizedMatch(i.code, posStr) : isNormalizedMatch(i.positionDescriptionLabel || i.description, posStr)
        ) : null;
        const codeObj = mscCodeStr ? codeArray.find(c => isNormalizedMatch(c.code || c.maintenanceSalaryCombinationCodeCode, mscCodeStr)) : null;

        let benefitErrors = [];
        const isDisregardOtherFilter = empTypeObj?.maintenanceSalaryCombinationDisregardOtherFilter ?? false;

        // Base Validations
        if (!effectiveDate) benefitErrors.push("Effective Date is required");

        if (!empTypeStr) benefitErrors.push("Employee Type is required");
        else if (!empTypeObj) benefitErrors.push(`Employee Type '${empTypeStr}' not found`);

        if (!mscCodeStr) benefitErrors.push("Salary Code is required");
        else if (!codeObj) benefitErrors.push(`Salary Code '${mscCodeStr}' not found`);

        // Group Validation (Check if present in DB or Sheet 2)
        if (!operationBenefitGroupStr) {
          benefitErrors.push("Group is required");
        } else {
          const groupInDb = allBenefitGroups.some(g => isNormalizedMatch(g.description, operationBenefitGroupStr));
          const groupInExcel = excelGroupNames.has(operationBenefitGroupStr.toLowerCase());

          if (!groupInDb && !groupInExcel) {
            benefitErrors.push(`Group '${operationBenefitGroupStr}' not found in DB or Group sheet`);
          }
        }

        if (isNaN(benefitRate) || benefitRate <= 0) benefitErrors.push("Operation Benefit Amount must be > 0");

        // Conditional Department & Position Validations
        if (isDisregardOtherFilter !== true) {
          if (!deptStr) benefitErrors.push("Department Code is required");
          else if (!deptObj) benefitErrors.push(`Dept '${deptStr}' not found`);

          if (!posStr) benefitErrors.push("Position Code is required");
          else if (!posObj) benefitErrors.push(`Pos '${posStr}' not found`);
        }

        const resolvedEmpTypeId = empTypeObj?.id;
        const resolvedDeptId = isDisregardOtherFilter ? null : deptObj?.id;
        const resolvedPosId = isDisregardOtherFilter ? null : posObj?.id;
        const resolvedCodeId = codeObj?.id;

        // Verify MSC combination exists in DB or Sheet 1
        let mscExistsInDb = false;
        if (allRules && allRules.length > 0) {
          mscExistsInDb = allRules.some(r => {
            const matchesEmp = r.employeeTypeId === resolvedEmpTypeId;
            const matchesCode = r.maintenanceSalaryCombinationCodeId === resolvedCodeId;
            const matchesDept = isDisregardOtherFilter ? true : r.departmentId === resolvedDeptId;
            const matchesPos = isDisregardOtherFilter ? true : r.positionId === resolvedPosId;
            return matchesEmp && matchesCode && matchesDept && matchesPos;
          });
        }

        const comboKey = buildMscComboKey(resolvedEmpTypeId, resolvedDeptId, resolvedPosId, resolvedCodeId);
        const mscExistsInExcel = excelMscKeys.has(comboKey);

        if (!mscExistsInDb && !mscExistsInExcel) {
          benefitErrors.push(`Matching MSC Rule combination not found in DB or Rules sheet`);
        }

        benefitsData.push({
          effectiveDate,
          employeeType: empTypeStr,
          department: isDisregardOtherFilter ? "" : deptStr,
          position: isDisregardOtherFilter ? "" : posStr,
          code: mscCodeStr,
          group: operationBenefitGroupStr,
          rate: benefitRate,
          errors: benefitErrors.join(", "),
          // Resolved Payload References
          employeeTypeId: resolvedEmpTypeId,
          departmentId: resolvedDeptId,
          positionId: resolvedPosId,
          maintenanceSalaryCombinationCodeId: resolvedCodeId,
          operationBenefitGroup: operationBenefitGroupStr,
          operationBenefitAmount: benefitRate
        });
      });

      // Update States
      setMscRules(rulesData);
      setBenefitGroups(groupsData);
      setBenefits(benefitsData);
    } catch (err) {
      console.error(err);
      showGeneralMessage("Error processing Excel. Please use the correct template.", false);
    } finally {
      setIsLoading(false);
    }
  };

  // Called when user clicks "Yes, Continue" inside the ConfirmationModal
  const handleConfirmUpload = () => {
    setShowConfirmModal(false);

    // Construct payload strictly sending rates along with effectiveDate to be written to History
    const payload = {
      mscRules: mscRules
        .filter(r => !r.errors)
        .map(r => ({
          effectiveDate: r.effectiveDate,
          employeeTypeId: r.employeeTypeId,
          departmentId: r.departmentId,
          positionId: r.positionId,
          maintenanceSalaryCombinationCodeId: r.maintenanceSalaryCombinationCodeId,
          dailyRate: r.dailyRate,
          maintenanceSalaryCombinationDailyRate: r.maintenanceSalaryCombinationDailyRate
        })),
      benefitGroups: benefitGroups
        .filter(g => !g.errors)
        .map(g => ({
          description: g.description
        })),
      benefits: benefits
        .filter(b => !b.errors)
        .map(b => ({
          effectiveDate: b.effectiveDate,
          employeeTypeId: b.employeeTypeId,
          departmentId: b.departmentId,
          positionId: b.positionId,
          maintenanceSalaryCombinationCodeId: b.maintenanceSalaryCombinationCodeId,
          rate: b.rate,
          operationBenefitGroup: b.operationBenefitGroup,
          operationBenefitAmount: b.operationBenefitAmount
        }))
    };

    onUpload(payload, user?.id);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-6xl rounded-lg shadow-2xl flex flex-col h-[85vh]">

        {/* Header */}
        <div className="px-6 py-4 border-b flex justify-between items-center bg-gray-50 rounded-t-lg">
          <h2 className="text-xl font-bold text-blue-900 tracking-tight">Bulk Import Configuration</h2>
          <CloseIcon className="cursor-pointer text-gray-400 hover:text-red-500 transition-colors" onClick={onClose} />
        </div>

        <div className="p-6 flex-1 overflow-hidden flex flex-col">
          {/* File Input */}
          <div className="mb-6 bg-blue-50 p-4 rounded-lg border border-blue-100 flex gap-4 items-center">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".xlsx"
              className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-bold file:bg-blue-600 file:text-white hover:file:bg-blue-700 cursor-pointer"
            />
            {isLoading && (
              <span className="text-blue-600 animate-pulse font-bold flex items-center gap-2">
                <span className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></span>
                Parsing Data...
              </span>
            )}
          </div>

          {/* Tabs */}
          <div className="flex border-b mb-4">
            {["Salary Rules", "Benefit Groups", "Rule Benefits"].map((label, idx) => (
              <button
                key={label}
                onClick={() => setActiveTab(idx)}
                className={`px-6 py-3 font-bold text-sm border-none cursor-pointer transition-all ${activeTab === idx
                  ? "text-blue-600 border-b-2 border-blue-600 bg-blue-50/50"
                  : "text-gray-400 bg-transparent hover:text-gray-600"
                  }`}
              >
                {label} ({idx === 0 ? mscRules.length : idx === 1 ? benefitGroups.length : benefits.length})
              </button>
            ))}
          </div>

          {/* Table Container */}
          <div className="flex-1 overflow-auto border border-gray-200 rounded-md shadow-inner bg-white">
            {activeTab === 0 && (
              <CreateTable
                tableName="RulesPreview"
                headers={["Effective Date", "Type", "Dept", "Pos", "Code", "Rate", "Status/Errors"]}
                accessors={["effectiveDate", "employeeType", "department", "position", "code", "dailyRate", "errors"]}
                data={mscRules}
                customCellRenderer={customCellRenderer}
                currentPage={pageRules}
                setCurrentPage={setPageRules}
                itemsPerPage={itemsPerPage}
                totalItems={mscRules.length}
              />
            )}

            {activeTab === 1 && (
              <CreateTable
                tableName="GroupsPreview"
                headers={["Group Name", "Status/Errors"]}
                accessors={["description", "errors"]}
                data={benefitGroups}
                customCellRenderer={customCellRenderer}
                currentPage={pageGroups}
                setCurrentPage={setPageGroups}
                itemsPerPage={itemsPerPage}
                totalItems={benefitGroups.length}
              />
            )}

            {activeTab === 2 && (
              <CreateTable
                tableName="BenefitsPreview"
                headers={["Effective Date", "Type", "Dept", "Pos", "Code", "Group", "Amount", "Status/Errors"]}
                accessors={["effectiveDate", "employeeType", "department", "position", "code", "operationBenefitGroup", "rate", "errors"]}
                data={benefits}
                customCellRenderer={customCellRenderer}
                currentPage={pageBenefits}
                setCurrentPage={setPageBenefits}
                itemsPerPage={itemsPerPage}
                totalItems={benefits.length}
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-end gap-3 rounded-b-lg">
          <button onClick={onClose} className="px-6 py-2 bg-white border border-gray-300 rounded font-bold text-gray-500 cursor-pointer hover:bg-gray-100">
            CANCEL
          </button>
          <button
            disabled={
              mscRules.some((r) => r.errors) ||
              benefitGroups.some((g) => g.errors) ||
              benefits.some((b) => b.errors) ||
              (mscRules.length === 0 && benefitGroups.length === 0 && benefits.length === 0) ||
              isLoading
            }
            onClick={() => setShowConfirmModal(true)}
            className="px-10 py-2 bg-blue-600 text-white border-none rounded font-bold cursor-pointer disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-blue-700 transition-all shadow-md"
          >
            CONFIRM BULK UPLOAD
          </button>
        </div>
      </div>

      <ConfirmationModal
        visible={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmUpload}
        title="Confirm Bulk Upload"
        message="Uploading this file will create or override existing records in the database with matching dates and setup combinations. This action cannot be reversed. Do you want to proceed?"
        confirmText="Yes, Proceed"
        cancelText="Cancel"
        type="warning"
      />
    </div>
  );
};

export default MaintenanceSalaryCombinationV2UploadModal;